import React, { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { submitQuote, getErrorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const initial = { name: "", email: "", phone: "", company: "", service: "", budget: "", message: "" };

const QuoteDialog = ({ open, onOpenChange }) => {
  const { t, c, isRTL } = useLang();
  const q = t.quote;
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error(q.selectService);
      return;
    }
    setLoading(true);
    try {
      await submitQuote(form);
      setDone(true);
      toast.success(q.toast);
    } catch (err) {
      toast.error(getErrorMessage(err, t.common.error));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setDone(false); setForm(initial); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid="quote-dialog" dir={isRTL ? "rtl" : "ltr"}>
        {done ? (
          <div className="flex flex-col items-center py-10 text-center" data-testid="quote-success">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-8 w-8" /></span>
            <h3 className="mt-5 font-heading text-2xl font-bold text-brand-navy">{q.thanks}, {form.name.split(" ")[0]}!</h3>
            <p className="mt-2 max-w-sm text-slate-600">{q.success}</p>
            <button type="button" onClick={() => handleOpenChange(false)} className="btn-primary mt-8 bg-brand-teal text-white hover:bg-brand-teal-dark">{q.close}</button>
          </div>
        ) : (
          <>
            <DialogHeader className={isRTL ? "text-start sm:text-start" : ""}>
              <DialogTitle className="font-heading text-2xl text-brand-navy">{q.title}</DialogTitle>
              <DialogDescription>{q.desc}</DialogDescription>
            </DialogHeader>
            <form onSubmit={onSubmit} className="mt-2 grid gap-4 sm:grid-cols-2" data-testid="quote-form">
              <div className="space-y-1.5"><Label htmlFor="q-name">{q.name}</Label><Input id="q-name" required value={form.name} onChange={set("name")} placeholder="John Doe" data-testid="quote-name-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="q-email">{q.email}</Label><Input id="q-email" type="email" required value={form.email} onChange={set("email")} placeholder="john@company.com" data-testid="quote-email-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="q-phone">{q.phone}</Label><Input id="q-phone" value={form.phone} onChange={set("phone")} placeholder="+971 50 000 0000" data-testid="quote-phone-input" /></div>
              <div className="space-y-1.5"><Label htmlFor="q-company">{q.company}</Label><Input id="q-company" value={form.company} onChange={set("company")} data-testid="quote-company-input" /></div>
              <div className="space-y-1.5">
                <Label>{q.service}</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="quote-service-select"><SelectValue placeholder={q.servicePh} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {c.OFFERING_GROUPS.map((g) => (
                      <React.Fragment key={g.group}>
                        <div className="px-2 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange">{g.group}</div>
                        {g.items.map((i) => <SelectItem key={i.slug} value={i.title}>{i.title}</SelectItem>)}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{q.budget}</Label>
                <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                  <SelectTrigger data-testid="quote-budget-select"><SelectValue placeholder={q.budgetPh} /></SelectTrigger>
                  <SelectContent>{q.budgets.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="q-message">{q.details}</Label><Textarea id="q-message" rows={4} value={form.message} onChange={set("message")} placeholder={q.detailsPh} data-testid="quote-message-input" /></div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} data-testid="quote-submit-button" className="btn-primary w-full bg-brand-orange text-white hover:bg-orange-600 disabled:opacity-60 sm:w-auto">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> {q.sending}</> : q.submit}
                </button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
